import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { environment } from '../../../../../environments/environment';
import { BreadcrumbComponent } from '../../../../shared/components/breadcrumb/breadcrumb.component';
import { LucideModule } from '../../../../shared/modules/lucide.module';
import { PrazoAtendimento } from '../../models/prazo-atendimento.model';
import { EnderecoPostoPresencial, PostoPresencialPublico } from '../models/posto-presencial-publico.model';
import {
  PRESENCIAL_NAO_PUBLICADO,
  consultaEnderecoPosto,
  enderecoPosto,
  prazosOuvidoria,
  responsavelOuvidoria,
  telefoneHref,
  telefoneOuvidoria,
} from '../prazos-ouvidoria';
import { PostoPresencialOuvidoriaService } from '../services/posto-presencial-ouvidoria.service';

export interface PostoOuvidoria {
  id: string;
  nome: string;
  tipo: string;
  endereco: EnderecoPostoPresencial;
  bairro: string;
  /** Já formatado para leitura; vazio quando a ouvidoria não tem telefone. */
  telefone: string;
  email: string;
  horario: string;
  /** Nunca vazio: o nome do responsável ou "em atualização", como na página `/ouvidoria`. */
  responsavel: string;
  /** Cada posto responde pelos seus próprios prazos. */
  prazos: readonly PrazoAtendimento[];
}

const TODOS = '';

/** O badge do card: toda ouvidoria presencial é exibida como posto de atendimento. */
const TIPO_POSTO = 'Posto de Atendimento';

@Component({
  selector: 'app-ouvidoria-presencial',
  templateUrl: './ouvidoria-presencial.component.html',
  styleUrls: ['./ouvidoria-presencial.component.css'],
  imports: [BreadcrumbComponent, FormsModule, LucideModule],
})
export class OuvidoriaPresencialComponent {
  private readonly sanitizer = inject(DomSanitizer);
  private readonly publico = inject(PostoPresencialOuvidoriaService);

  /** Enquanto a leitura não termina, a tela não exibe nem os postos, nem o estado vazio, nem o mapa. */
  readonly carregando = this.publico.carregando;
  readonly naoPublicado = PRESENCIAL_NAO_PUBLICADO;

  readonly trilha = [{ titulo: 'Ouvidoria', link: '/ouvidoria' }];

  /**
   * As ouvidorias presenciais do órgão, na ordem da API. O contrato não tem código, então o `id` é a
   * posição na lista — basta para o `track` e para o posto selecionado no mapa.
   */
  readonly postos = computed<readonly PostoOuvidoria[]>(() =>
    this.publico.postos().map((posto, indice) => paraPosto(posto, indice))
  );

  readonly busca = signal('');
  readonly unidadeSelecionada = signal(TODOS);
  readonly bairroSelecionado = signal(TODOS);
  readonly horarioSelecionado = signal(TODOS);

  /** Com um único posto a lista vira um card só, sem filtro — como no protótipo. */
  readonly varios = computed(() => this.postos().length > 1);

  readonly unidades = computed(() => this.opcoes((posto) => posto.nome));

  readonly bairros = computed(() => this.opcoes((posto) => posto.bairro));

  readonly horarios = computed(() => this.opcoes((posto) => posto.horario));

  readonly filtrados = computed<readonly PostoOuvidoria[]>(() => {
    const termo = this.busca().trim().toLowerCase();
    const unidade = this.unidadeSelecionada();
    const bairro = this.bairroSelecionado();
    const horario = this.horarioSelecionado();

    return this.postos().filter((posto) => {
      if (unidade !== TODOS && posto.nome !== unidade) return false;
      if (bairro !== TODOS && posto.bairro !== bairro) return false;
      if (horario !== TODOS && posto.horario !== horario) return false;
      if (!termo) return true;

      return [posto.nome, posto.tipo, posto.bairro, this.endereco(posto)]
        .join(' ')
        .toLowerCase()
        .includes(termo);
    });
  });

  private readonly selecionadoId = signal('');

  /** O posto do mapa: o escolhido no card ou, por padrão, o primeiro da lista visível. */
  readonly selecionado = computed<PostoOuvidoria | null>(() => {
    const visiveis = this.filtrados();

    return visiveis.find((posto) => posto.id === this.selecionadoId()) ?? visiveis[0] ?? null;
  });

  /** Sem posto não há mapa — nem uma localização de reserva. */
  readonly mapaUrl = computed<SafeResourceUrl | null>(() => {
    const url = this.montarUrlDoMapa();

    return url ? this.sanitizer.bypassSecurityTrustResourceUrl(url) : null;
  });

  endereco(posto: PostoOuvidoria): string {
    return enderecoPosto(posto.endereco);
  }

  selecionar(posto: PostoOuvidoria): void {
    this.selecionadoId.set(posto.id);
  }

  limparFiltros(): void {
    this.busca.set('');
    this.unidadeSelecionada.set(TODOS);
    this.bairroSelecionado.set(TODOS);
    this.horarioSelecionado.set(TODOS);
  }

  /** Abre a rota até o posto no Google Maps, em nova aba. */
  rotaUrl(posto: PostoOuvidoria): string {
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(this.consulta(posto))}`;
  }

  telefoneUrl(posto: PostoOuvidoria): string {
    return telefoneHref(posto.telefone);
  }

  emailUrl(posto: PostoOuvidoria): string {
    return `mailto:${posto.email}`;
  }

  /** Valores distintos de um campo; com menos de dois o filtro correspondente não é exibido. */
  private opcoes(campo: (posto: PostoOuvidoria) => string): readonly string[] {
    return [...new Set(this.postos().map(campo).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }

  private consulta(posto: PostoOuvidoria): string {
    return consultaEnderecoPosto(posto.endereco);
  }

  /**
   * Com a chave da Embed API configurada, o modo `search` planta um marcador por posto; sem chave,
   * o mapa público mostra um posto por vez e o card escolhido define qual.
   */
  private montarUrlDoMapa(): string | null {
    const chave = environment.googleMapsKey;
    const visiveis = this.filtrados();
    const alvo = this.selecionado();

    if (!alvo) return null;

    if (chave && visiveis.length > 1) {
      const termo = visiveis.map((posto) => this.consulta(posto)).join(' | ');

      return `https://www.google.com/maps/embed/v1/search?key=${chave}&q=${encodeURIComponent(termo)}&zoom=12`;
    }

    if (chave) {
      return `https://www.google.com/maps/embed/v1/place?key=${chave}&q=${encodeURIComponent(
        this.consulta(alvo)
      )}&zoom=16`;
    }

    return `https://maps.google.com/maps?q=${encodeURIComponent(this.consulta(alvo))}&z=16&output=embed`;
  }
}

/** Da resposta da API para o card: endereço, telefone, responsável e prazos já prontos para a tela. */
function paraPosto(posto: PostoPresencialPublico, indice: number): PostoOuvidoria {
  return {
    id: String(indice),
    nome: posto.nome,
    tipo: TIPO_POSTO,
    endereco: posto.endereco,
    bairro: posto.endereco?.bairro?.trim() ?? '',
    telefone: telefoneOuvidoria(posto.telefonePrincipal),
    email: posto.email?.trim() ?? '',
    horario: posto.horarioAtendimento?.trim() ?? '',
    responsavel: responsavelOuvidoria(posto.responsavel),
    prazos: prazosOuvidoria({
      prazoResposta: posto.prazoResposta,
      permiteContestacao: posto.permiteContestacao,
      prazoContestacao: posto.prazoContestacao,
      unidade: posto.unidadePrazo,
    }),
  };
}
